<template>
  <div
    v-if="layout === 'full'"
    class="flex flex-col md:flex-row flex-1 relative justify-center p-3 gap-4 md:gap-5 py-4 px-4 md:px-6 border rounded-lg shadow-sm bg-white text-sm leading-relaxed text-left min-w-0 md:min-w-[250px] w-full md:w-fit"
    :class="active ? 'border-primary' : 'border-gray-200'"
  >
    <span
      v-if="dutyLabel"
      :class="[
        'absolute top-2 left-2 text-xs md:text-sm text-gray-600 border px-1 py-0.5 rounded mb-2',
        dutyBgColor
      ]"
    >
      {{ capitalizedDutyLabel }}
    </span>

    <!-- `pt-4` só existe para abrir espaço para o chip absoluto acima; sem chip,
         o respiro no topo sobraria vazio.
         `min-w-0` + `truncate` no `DoctorInfo`: a identidade precisa poder
         encolher. Travá-la com `shrink-0` faz um nome longo (38 caracteres já
         basta) roubar toda a linha, a coluna de requisitos cair para ~97px e os
         quatro valores virarem só "…" — e o card estourar containers estreitos
         como a sidebar do Pós-Plantão. Quem cede primeiro é o nome, que tem
         `…` + tooltip próprio; a coluna tem piso garantido abaixo. -->
    <div class="flex flex-col justify-center min-w-0" :class="{ 'pt-4': dutyLabel }">
      <DoctorInfo
        :name="doctorName"
        :subtitle="doctorCrm"
        :avatar-url="avatarUrl"
        :nps-score="npsScore"
        size="lg"
        :truncate="true"
      />
    </div>

    <!-- Um único gate para os divisores e o bloco: com três `v-if` irmãos e
         idênticos, esquecer um deixaria um divisor órfão renderizado sem o
         bloco ao lado. O `<template>` não gera elemento, então o flex do pai
         continua igual. -->
    <template v-if="canReadScheduleRequirements">
      <div class="hidden md:block w-px bg-gray-200 h-auto self-stretch"></div>
      <div class="block md:hidden w-full h-px bg-gray-200 self-stretch"></div>

      <!-- `min-w-0` habilita o `truncate` dos valores (filho flex não encolhe
           abaixo do conteúdo sem isso); `md:min-w-[200px]` é o piso que impede o
           oposto: com `flex-basis: 0%` e sem mínimo, um nome longo empurrava a
           coluna para ~57px e os valores ficavam com largura zero — nem texto
           nem "…", só os rótulos. -->
      <div
        class="flex flex-1 min-w-0 md:min-w-[200px] flex-col gap-1 text-center justify-center md:text-left"
      >
        <!-- `max-w-full` + `truncate` junto do `nowrap`: a pill é `w-fit`, então
             sem teto ela não quebrava nem encolhia — vazava ~69px para fora do
             card, e o modal (`overflow-hidden`) cortava o excesso. -->
        <p
          v-if="npsScore != null && npsColors"
          class="text-base px-2 py-0.5 rounded w-fit max-w-full border font-semibold whitespace-nowrap truncate"
          :style="{
            color: npsColors.color,
            backgroundColor: npsColors.backgroundColor,
            borderColor: npsColors.color
          }"
        >
          Avaliação Paciente: <strong>{{ npsScore }}</strong>
        </p>
        <!-- Rótulo com `shrink-0` (nunca quebra) + valor com `truncate`: o campo
             ocupa exatamente uma linha e o que não couber vira "…". O tooltip
             carrega o texto completo, no mesmo padrão do `HistoryCard`
             (`activator="parent"`, portanto ancorado no próprio `<p>`).
             Tudo prefixado com `md:`: abaixo disso o card empilha, sobra altura
             e o `v-tooltip` é hover-only — cortar texto no celular esconderia o
             valor sem forma de recuperá-lo. No mobile continua quebrando linha,
             mesma decisão aplicada ao layout `simple`. -->
        <p
          v-for="field in requirementFields"
          :key="field.label"
          class="text-base md:flex md:items-baseline md:gap-1 md:justify-start"
          :data-testid="field.testId"
        >
          <strong class="md:shrink-0">{{ field.label }}:</strong>
          <span class="md:truncate md:min-w-0">{{ field.value }}</span>
          <v-tooltip activator="parent" location="top start" max-width="320">
            {{ field.label }}: {{ field.value }}
          </v-tooltip>
        </p>
      </div>
    </template>
  </div>

  <div
    v-else-if="layout === 'simple'"
    class="relative flex flex-col items-center justify-start p-4 gap-3 border rounded-lg shadow-sm bg-white text-sm text-center min-w-[200px] w-full md:w-fit"
    :class="active ? 'border-primary' : 'border-gray-200'"
  >
    <span
      v-if="dutyLabel"
      :class="[
        'absolute top-2 left-2 text-xs text-gray-600 border px-1 py-0.5 rounded',
        dutyBgColor
      ]"
    >
      {{ capitalizedDutyLabel }}
    </span>

    <div class="flex flex-col justify-center mt-4">
      <DoctorInfo
        :name="doctorName"
        :subtitle="doctorCrm"
        :avatar-url="avatarUrl"
        :nps-score="npsScore"
        size="md"
      />
    </div>

    <div class="w-full h-px bg-gray-200 self-stretch"></div>

    <div class="flex flex-col gap-1 text-left">
      <p
        v-if="npsScore != null && npsColors"
        class="text-base px-2 py-0.5 rounded w-fit border font-semibold"
        :style="{
          color: npsColors.color,
          backgroundColor: npsColors.backgroundColor,
          borderColor: npsColors.color
        }"
      >
        Avaliação Paciente: <strong>{{ npsScore }}</strong>
      </p>
      <!-- O layout `simple` mantém a estrutura antiga de propósito: é um card
           estreito e vertical, onde quebrar linha é aceitável — nada de
           `truncate` aqui. Mas rótulos e fallbacks vêm da mesma lista do layout
           `full` (só os dois primeiros campos), para renomear um rótulo ou
           acrescentar um campo não deixar este layout para trás. -->
      <p v-for="field in simpleRequirementFields" :key="field.label" class="text-base">
        <strong>{{ field.label }}:</strong>
        {{ field.value }}
      </p>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { capitalizeFirstLetter } from '@/utils/string'
import { getDutyTagBgClass, getDutyTagBorderClass } from '@/utils/doctorStation'
import { classifyNpsScore } from '@/utils/nps'
import { computed } from 'vue'
import { usePermissionsStore } from '@/pinia-store/usePermissionsStore'
import DoctorInfo from '@/components/DoctorInfo.vue'
import {
  AuthorizationPermissionCheck,
  AuthorizationPermissions,
  AuthorizationResourcesAndSubjects
} from '@/types/authorization'

const props = withDefaults(
  defineProps<{
    formation: string
    team: string
    position?: string
    shift?: string
    dutyLabel: string
    doctorName: string
    doctorCrm: string
    active?: boolean
    layout?: 'full' | 'simple'
    scheduleId?: number
    npsScore?: number
    avatarUrl?: string | null
    // Sobrepõe a checagem de permissão do bloco de requisitos (ex: telas mockadas).
    // Quando undefined, mantém o comportamento padrão baseado em scheduleId + permissão.
    showRequirements?: boolean
  }>(),
  {
    layout: 'full',
    position: '',
    shift: '',
    // NÃO REMOVER: prop `Boolean` opcional sem default explícito é convertida
    // pelo Vue em `false` quando o consumidor não a passa — nunca `undefined`.
    // Sem este default, o override abaixo engole o caminho de permissão e o
    // bloco de requisitos desaparece para todo mundo (regressão do PR #1936).
    showRequirements: undefined
  }
)

const permissionsStore = usePermissionsStore()

const canReadScheduleRequirements = computed(() => {
  // Tri-state: só entra no override quando o consumidor passou a prop de fato.
  // Depende do default explícito `showRequirements: undefined` no
  // `withDefaults` acima — sem ele o Vue entrega `false` para quem não passa a
  // prop, esta condição fica sempre verdadeira e a permissão nunca é checada.
  if (props.showRequirements !== undefined) {
    return props.showRequirements
  }

  if (!props.scheduleId) {
    return false
  }

  const checkObject: AuthorizationPermissionCheck = {
    permission: AuthorizationPermissions.ScheduleRequerimentReading,
    resource: {
      objectType: AuthorizationResourcesAndSubjects.Schedule,
      objectId: props.scheduleId
    }
  }

  return permissionsStore.check(checkObject)
})

const capitalizedDutyLabel = computed(() => {
  return props.dutyLabel ? capitalizeFirstLetter(props.dutyLabel) : ''
})

const dutyBgColor = computed(() => {
  return `${getDutyTagBgClass(props.dutyLabel)} ${getDutyTagBorderClass(props.dutyLabel)}`
})

const npsColors = computed(() => (props.npsScore != null ? classifyNpsScore(props.npsScore) : null))

// Fonte única dos valores e dos fallbacks, consumida pelos dois layouts. O
// gênero difere de propósito: posição e turno são configuração de escala que
// pode não estar vinculada ("não definida/definido"); formação e equipe são
// dados preenchidos ("não informado") — ver #2260.
const requirementValues = computed(() => ({
  formation: props.formation?.trim().length ? props.formation : 'Não informado',
  team: props.team?.trim().length ? props.team : 'Não informado',
  position: props.position?.trim().length ? props.position : 'Não definida',
  shift: props.shift?.trim().length ? capitalizeFirstLetter(props.shift) : 'Não definido'
}))

// Percorrido por `v-for` no layout `full`: a marcação de rótulo + valor truncado
// + tooltip é idêntica nos quatro campos, e repeti-la quatro vezes já custou uma
// divergência antes (o fallback de Posição/Turno ficou fora de sincronia).
const requirementFields = computed(() => [
  { label: 'Formação', value: requirementValues.value.formation, testId: 'duty-card-formation' },
  { label: 'Equipe', value: requirementValues.value.team, testId: 'duty-card-team' },
  { label: 'Posição', value: requirementValues.value.position, testId: 'duty-card-position' },
  { label: 'Turno', value: requirementValues.value.shift, testId: 'duty-card-shift' }
])

// O layout `simple` sempre mostrou só Formação e Equipe — posição e turno não
// cabem num card estreito. Derivado da mesma lista para os rótulos existirem em
// um único lugar.
const simpleRequirementFields = computed(() => requirementFields.value.slice(0, 2))
</script>
